import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import toast from "react-hot-toast";

import {
  usePaymentsConfigQuery,
  useQuickPayCreateIntentMutation,
} from "../store/api.ts";
import { getFirstError } from "../utils.js";
import { getAuthToken } from "../utils/authSession.js";

// react-snap prerenders every route in a headless browser at build time.
// Wallets can never work there, and loading Stripe.js would only slow the
// prerender down for nothing.
const isPrerender =
  typeof window === "undefined" ||
  (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap");

// loadStripe returns a promise that must be created once per key, never inside
// a render. Keyed by the publishable key so a key change (test -> live) still
// produces a fresh loader.
const stripePromises = new Map();

const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  if (!stripePromises.has(publishableKey)) {
    stripePromises.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromises.get(publishableKey);
};

// Shipping is free everywhere we ship, and the backend adds nothing on top, so
// one rate is enough. The wallet sheet refuses to open without at least one
// whenever shippingAddressRequired is on.
const SHIPPING_RATES = [
  {
    id: "free-standard",
    displayName: "Free Standard (3-7 business days)",
    amount: 0,
  },
];

const ALLOWED_SHIPPING_COUNTRIES = ["US"];

/**
 * Turn the checkout form's fields into the structured shape Stripe wants for
 * PaymentIntent.shipping.
 *
 * Only used on the payment-step placement. The express placements get shipping
 * from the ConfirmationToken, which already carries it in this shape.
 */
const buildStripeShipping = (payer) => {
  if (!payer?.address1 || !payer?.country) return undefined;

  return {
    name: [payer.firstName, payer.lastName].filter(Boolean).join(" ") || "Customer",
    phone: payer.phone || undefined,
    address: {
      line1: payer.address1,
      line2: payer.address2 || undefined,
      city: payer.city || undefined,
      state: payer.state || undefined,
      postal_code: payer.postalCode || undefined,
      country: payer.country,
    },
  };
};

const ApplePayLoader = ({
  buttonHeight,
  applePayTheme = "white-outline",
  buttonRadius = null,
}) => {

  const isDark = applePayTheme === "black";
  const resolvedRadius = buttonRadius === null || buttonRadius === undefined ? 5 : buttonRadius;

  return (
    <div
      className={[
        "flex w-full items-center justify-center",
        "box-border mt-1",
        isDark
          ? "border border-black bg-black"
          : "border border-black bg-white",
      ].join(" ")}
      style={{
        height: `${buttonHeight}px`,
        minHeight: `${buttonHeight}px`,
        borderRadius: `${resolvedRadius}px`,
      }}
      role="status"
      aria-label="Loading Apple Pay"
    >
      <span
        className={[
          "h-4 w-4 shrink-0 animate-spin rounded-full border-2",
          isDark
            ? "border-white/30 border-t-white"
            : "border-black/20 border-t-black",
        ].join(" ")}
      />
    </div>
  );
};

const QuickPayForm = ({
  express,
  items,
  bundles,
  amount,
  payer,
  disabled,
  buttonHeight,
  buttonRadius,
  applePayTheme,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [createIntent] = useQuickPayCreateIntentMutation();

  // The element renders nothing at all when the browser has no wallet. Until
  // 'ready' fires we do not know, so we keep the slot collapsed rather than
  // reserving space that may never fill.
  const [walletStatus, setWalletStatus] = useState("checking");
  const hasWallet = walletStatus === "available";
  const isWalletChecking = walletStatus === "checking";

  // A signed-in shopper's email already lives on their account — no need to
  // ask the sheet for it. Re-read on the app's own login/logout event rather
  // than once at mount, so a shopper who logs in mid-visit gets the shorter
  // sheet without a page reload. Changing `options` below only reconfigures
  // the already-mounted Element (@stripe/react-stripe-js calls element.update()
  // for every key except paymentRequest) — never a remount, so this cannot
  // produce the blink a live-validity-driven mode switch would.
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getAuthToken()));
  useEffect(() => {
    const syncAuth = () => setIsAuthenticated(Boolean(getAuthToken()));
    window.addEventListener("eveness-auth-changed", syncAuth);
    return () => window.removeEventListener("eveness-auth-changed", syncAuth);
  }, []);

  const options = useMemo(
    () => ({
      buttonHeight,
      // Apple Pay is the only wallet this build ships with. 'always', not
      // 'auto': 'auto' only shows the button when the browser can pay with it
      // right now (Safari with a card already in Wallet) — everyone else sees
      // nothing. 'always' keeps the button up regardless and lets Stripe/Apple
      // fall back to their own device-setup flow — on desktop non-Safari, a QR
      // code that hands the payment to an iPhone — instead of silently
      // disappearing.
      //
      // Google Pay and Link both ride on the same `card` payment type, so
      // paymentMethodTypes below cannot exclude them — they have to be turned
      // off by name here. To drive the express placement locally without an
      // Apple device, flip googlePay to 'always' and revert it afterwards —
      // it runs the identical create-intent/confirm/webhook path.
      paymentMethods: {
        applePay: "always",
        googlePay: "never",
        link: "never",
        paypal: "never",
        amazonPay: "never",
        klarna: "never",
      },
      buttonType: {
        applePay: "plain",
      },
      buttonTheme: {
        applePay: applePayTheme,
      },
      // Express only. On the payment step the shopper has already typed an
      // address and an email, and overriding either with what the wallet holds
      // would silently discard what they entered.
      //
      // shippingAddressRequired stays true either way — there is no saved
      // address anywhere in the schema, signed in or not. emailRequired is
      // the one that depends on auth: a signed-in shopper's email already
      // lives on their account (resolve_quick_pay_payer skips it for them),
      // so only a guest is asked.
      //
      // Never phoneNumberRequired: phone is display-only for us, and requiring
      // a field the wallet cannot supply is the documented way to make the
      // button disappear.
      ...(express
        ? {
            emailRequired: !isAuthenticated,
            shippingAddressRequired: true,
            allowedShippingCountries: ALLOWED_SHIPPING_COUNTRIES,
          }
        : {}),
    }),
    [express, buttonHeight, applePayTheme, isAuthenticated],
  );

  // Two separate events, both needed. onReady is a one-time snapshot taken at
  // mount; in 'always' mode Apple's own async device-eligibility check can
  // still be resolving when it fires, so a wallet that only becomes known a
  // moment later is missed unless onAvailablePaymentMethodsChange is also
  // watched — that one fires again whenever the answer changes.
  const handleReady = useCallback(({ availablePaymentMethods }) => {
    setWalletStatus(availablePaymentMethods ? "available" : "unavailable");
  }, []);

  // Fires the moment the button is tapped, before the sheet opens. Stripe gives
  // us one second to answer, so everything here must already be in hand.
  const handleClick = useCallback(
    ({ resolve }) => {
      resolve({
        lineItems: [{ name: "Order total", amount }],
        ...(express ? { shippingRates: SHIPPING_RATES } : {}),
      });
    },
    [amount, express],
  );

  // Shipping is free regardless of where in the US the shopper picks, so this
  // only has to answer; there is no rate to recalculate. Not answering leaves
  // the sheet spinning.
  const handleShippingAddressChange = useCallback(({ resolve }) => {
    resolve({ shippingRates: SHIPPING_RATES });
  }, []);

  const handleConfirm = useCallback(
    async (event) => {
      if (!stripe || !elements || disabled) {
        event.paymentFailed({ reason: "fail" });
        return;
      }

      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          event.paymentFailed({ reason: "invalid_payment_data" });
          toast.error(submitError.message || "Payment could not be started.");
          return;
        }

        const body = { items, bundles: bundles || [] };
        let confirmationTokenId = null;

        if (express) {
          // Hand what the wallet collected to Stripe and keep only the ticket.
          // The server reads the real email and address back off it, so nothing
          // load-bearing passes through this browser.
          const { error: tokenError, confirmationToken } =
            await stripe.createConfirmationToken({ elements });

          if (tokenError) {
            event.paymentFailed({ reason: "fail" });
            toast.error(tokenError.message || "Payment could not be started.");
            return;
          }

          confirmationTokenId = confirmationToken.id;
          body.confirmation_token_id = confirmationTokenId;
        } else {
          // Payment step: the form is the source of truth. The server takes
          // these exactly as /cart-checkout/ does.
          Object.assign(body, {
            email: payer?.email || "",
            address: payer?.address || "",
            first_name: payer?.firstName || "",
            last_name: payer?.lastName || "",
            phone: payer?.phone || "",
          });
        }

        const { clientSecret, orderId } = await createIntent(body).unwrap();

        const confirmParams = {
          return_url: `${window.location.origin}/payment-success?order_id=${orderId}`,
        };

        if (express) {
          confirmParams.confirmation_token = confirmationTokenId;
        } else {
          // Without this Stripe has no shipping address on the intent, losing a
          // fraud signal and the evidence a dispute would need. Express gets it
          // from the token for free; here it has to be passed explicitly.
          const shipping = buildStripeShipping(payer);
          if (shipping) confirmParams.shipping = shipping;
        }

        const { error: confirmError } = await stripe.confirmPayment({
          clientSecret,
          confirmParams,
          // Where Stripe.js finds the card. With a token the card is already
          // inside it. Without one, Stripe.js has to read the authorised wallet
          // card out of the element, so `elements` must be passed.
          ...(express ? {} : { elements }),
          redirect: "if_required",
        });

        if (confirmError) {
          event.paymentFailed({ reason: "fail" });
          toast.error(confirmError.message || "Payment failed. Please try again.");
          return;
        }

        if (onSuccess) onSuccess(orderId);
        navigate(`/payment-success?order_id=${orderId}`);
      } catch (error) {
        event.paymentFailed({ reason: "fail" });
        console.error("Quick pay failed:", error);
        toast.error(
          getFirstError(error?.data || error) ||
            "Unable to complete payment. Please try again.",
        );
      }
    },
    [
      stripe,
      elements,
      disabled,
      items,
      bundles,
      express,
      payer,
      createIntent,
      navigate,
      onSuccess,
    ],
  );

  if (walletStatus === "unavailable") return null;

  return (
    <div
      // The element has no disabled state of its own, so the wrapper takes the
      // taps. handleConfirm re-checks, since pointer-events is only cosmetic.
      className={
        disabled ? "pointer-events-none opacity-50" : undefined
      }
      aria-disabled={disabled || undefined}
      style={{
        position: "relative",
        minHeight: `${buttonHeight}px`,
      }}
    >
      {/* Loader */}
      {isWalletChecking && (
        <div className="absolute inset-0 z-10">
          <ApplePayLoader
            buttonHeight={buttonHeight}
            applePayTheme={applePayTheme}
            buttonRadius={buttonRadius}
          />
        </div>
      )}
      <div
        style={{
          visibility: hasWallet ? "visible" : "hidden",
        }}
      >
        <ExpressCheckoutElement
          options={options}
          onReady={handleReady}
          onClick={handleClick}
          onConfirm={handleConfirm}
        // The element renders nothing when it cannot load, so without this a
        // configuration problem is indistinguishable from "no wallet here".
          onLoadError={({ error }) => {
            setWalletStatus("unavailable");
            console.error("Express checkout failed to load:", error);
          }}
          {...(express
            ? { onShippingAddressChange: handleShippingAddressChange }
            : {})}
        />
      </div>
    </div>
  );
};

/**
 * Apple Pay / Google Pay, in one of two placements.
 *
 *   <QuickPayButton express items={...} amount={...} />   product page, cart
 *   <QuickPayButton items={...} amount={...} payer={...} />   payment step
 *
 * Both mount the same ExpressCheckoutElement. "Express" describes where the
 * button sits — a shortcut past checkout, where the wallet supplies the email
 * and the address — not a different widget. On the payment step the shopper has
 * already filled the form, so the wallet is asked for nothing but a card and the
 * `payer` prop carries the details.
 *
 * The prop is fixed by the call site and never changes at runtime, so the
 * element mounts once and never remounts.
 *
 * @param {number} amount  Order total in cents. Must match what the backend
 *                         recomputes from the database, or the sheet shows one
 *                         figure and the shopper is charged another.
 */
export default function QuickPayButton({
  express = false,
  items,
  bundles,
  amount,
  payer,
  disabled = false,
  className = "",
  buttonHeight = 45,
  buttonRadius = null,
  applePayTheme = "white-outline",
  onSuccess,
}) {
  const {
    data: config,
    isLoading: isConfigLoading,
    isFetching: isConfigFetching,
    isError: isConfigError,
  } = usePaymentsConfigQuery();
  const stripePromise = useMemo(
    () => getStripePromise(config?.publishableKey),
    [config?.publishableKey],
  );

  const elementsOptions = useMemo(
    () => ({
      // Deferred intent: the PaymentIntent does not exist until the shopper has
      // authorised, so Elements is told the shape of the payment up front.
      mode: "payment",
      amount,
      currency: "usd",
      // MUST match payment_method_types on the PaymentIntent the backend
      // creates (core/views/user_views.py — payment_method_types=["card"]).
      // Without it the Element falls back to the Dashboard's own payment
      // method config and can render buttons (Link, Amazon Pay, Klarna) that
      // the intent then refuses to confirm — the shopper taps, and it fails.
      paymentMethodTypes: ["card"],
      // Required before createConfirmationToken({ elements }) is allowed.
      paymentMethodCreation: "manual",
      ...(buttonRadius !== null &&
      buttonRadius !== undefined
        ? {
            appearance: {
              variables: {
                borderRadius: `${buttonRadius ?? 5}px`,            
              },
            },
          }
        : {}),
    }),
    [amount, buttonRadius],
  );

  const hasItems = Array.isArray(items) && items.length > 0;
  const hasBundles = Array.isArray(bundles) && bundles.length > 0;

  // Elements refuses a non-positive amount, and quick pay needs at least one
  // standalone item or one bundle to create an order.
  if (isPrerender || !amount || amount <= 0 || (!hasItems && !hasBundles)) {
    return null;
  }

  if (!stripePromise) {
    if (isConfigError || (!isConfigLoading && !isConfigFetching)) {
      return null;
    }

    return (
      <div className={className}>
        <ApplePayLoader
          buttonHeight={buttonHeight}
          applePayTheme={applePayTheme}
          buttonRadius={buttonRadius}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <Elements stripe={stripePromise} options={elementsOptions}>
        <QuickPayForm
          express={express}
          items={items}
          bundles={bundles}
          amount={amount}
          payer={payer}
          disabled={disabled}
          buttonHeight={buttonHeight}
          buttonRadius={buttonRadius}
          applePayTheme={applePayTheme}
          onSuccess={onSuccess}
        />
      </Elements>
    </div>
  );
}
